import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  MailOutline,
  MarkEmailReadOutlined,
  ArrowBack,
  InfoOutlined,
  Send,
} from '@mui/icons-material';
import axios from 'axios';
import AuthLayout from './auth/AuthLayout';
import {
  authColors,
  fontStack,
  AuthField,
  AuthButton,
} from './auth/authStyles';

const ForgotPassword = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors de l'envoi du lien de réinitialisation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      brandTitle="Une sécurité qui vous suit partout."
      brandSubtitle="Réinitialisez votre mot de passe en quelques secondes, en toute sécurité. Un lien unique vous sera envoyé par email."
      error={error}
      success={
        success
          ? 'Un email contenant un lien de réinitialisation a été envoyé. Vérifiez votre boîte de réception (et vos spams).'
          : ''
      }
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Retour */}
        <Box
          component="button"
          type="button"
          onClick={onBackToLogin}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            p: 0,
            color: authColors.textMuted,
            fontFamily: fontStack,
            fontSize: '13px',
            '&:hover': { color: authColors.textPrimary },
          }}
        >
          <ArrowBack sx={{ fontSize: 16 }} />
          Retour à la connexion
        </Box>

        {/* Icône */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '20px',
            backgroundColor: '#1B3B2E',
            border: `1px solid ${authColors.accentGreenDark}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MarkEmailReadOutlined sx={{ fontSize: 28, color: authColors.accentGreen }} />
        </Box>

        {/* En-tête */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography sx={{ color: authColors.textPrimary, fontSize: { xs: '24px', md: '28px' }, fontWeight: 700, lineHeight: 1.2, fontFamily: fontStack }}>
            Réinitialiser votre mot de passe
          </Typography>
          <Typography sx={{ color: authColors.textMuted, fontSize: '13px', lineHeight: 1.5, fontFamily: fontStack }}>
            Entrez votre email, nous vous enverrons un lien de réinitialisation sécurisé.
          </Typography>
        </Box>

        {!success && (
          <>
            <AuthField
              label="Adresse email"
              icon={<MailOutline sx={{ color: authColors.accentGreen }} />}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.com"
              required
              autoComplete="email"
              accentBorder
            />

            {/* Encart info */}
            <Box
              sx={{
                display: 'flex',
                gap: '10px',
                p: '14px',
                borderRadius: '12px',
                backgroundColor: '#1B2A3F',
                border: '1px solid #1F3B5A',
              }}
            >
              <InfoOutlined sx={{ fontSize: 16, color: authColors.accentBlue, mt: '2px' }} />
              <Typography sx={{ color: authColors.textMuted, fontSize: '12px', lineHeight: 1.5, fontFamily: fontStack }}>
                Le lien expire au bout de 15 minutes. Vérifiez aussi vos spams.
              </Typography>
            </Box>

            <AuthButton loading={loading}>
              {!loading && <Send sx={{ fontSize: 16 }} />}
              {loading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation'}
            </AuthButton>
          </>
        )}
      </Box>
    </AuthLayout>
  );
};

export default ForgotPassword;
